import React, { Component } from 'react';
import { JobOfferQuestionnaire, JobOfferQuestionnairePage } from '../../../api/jobOffers/jobOffers.state';
import QuestionnairePage, { prefixes } from './questionnaire/QuestionnairePage';
import {
    QuestionQuestionnaireElementType,
    QuestionnaireAnswer,
    QuestionnaireElement,
    QuestionnaireAnswersDto,
    QuestionQuestionnaireAnswerType,
} from '../../../api/jobOffers/jobOffers.dto';
import * as yup from 'yup';
import _ from 'lodash';
import { FormikValues } from 'formik';
import { boundMethod } from 'autobind-decorator';
import { connect } from 'react-redux';
import { ThunkDispatch } from '../../../api/_commons/thunks.common';
import {
    JobOfferApplicationThunks,
    SubmitJobApplicationCommandError,
} from '../../../api/jobOfferApplication/jobOfferApplication.thunk';
import { State } from '../../../store/state';
import { RouteComponentProps, withRouter } from 'react-router';
import { RoutePath } from '../../../routes';
import { WithTranslation, withTranslation } from 'react-i18next';
import moment from 'moment';
import { FormValues } from './questionnaire/QuestionnaireExperience';
import { FormValues as EducationFormValues } from './questionnaire/QuestionnaireEducation';
import { FileUploadValue } from '@epicode/react-inspinia/dist/components/form/field/file/FileUpload';

interface OfferApplicationFormProps {
    questionnaireStructure: JobOfferQuestionnaire;
}

interface OfferApplicationFormStateProps {
    jobOfferId: string;
    formErrors: SubmitJobApplicationCommandError[];
    storedAnswers: QuestionnaireAnswersDto;
}

interface OfferApplicationFormDispatchProps {
    saveStaticQuestionsAnswers: (
        firstName: string,
        lastName: string,
        prefix: string,
        phoneNumber: string,
        email: string
    ) => void;
    saveAdditionalAnswers: (additionalAnswers: QuestionnaireAnswer[]) => void;
    submitForm: () => Promise<boolean>;
}

interface OfferApplicationFormState {
    activePageIdx: number;
    isSubmittingApplication: boolean;
}

interface OfferApplicationFormRouteProps {
    id: string;
}

class OfferApplicationForm extends Component<
    OfferApplicationFormProps &
        WithTranslation &
        OfferApplicationFormStateProps &
        OfferApplicationFormDispatchProps &
        RouteComponentProps<OfferApplicationFormRouteProps>,
    OfferApplicationFormState
> {
    state: OfferApplicationFormState = {
        activePageIdx: 0,
        isSubmittingApplication: false,
    };

    render() {
        return this.renderPage(this.currentPage);
    }

    private get currentPage(): JobOfferQuestionnairePage {
        const page = this.props.questionnaireStructure.pages.find(
            (page) => page.pageNumber === this.state.activePageIdx + 1
        );

        return page || { elements: [], pageNumber: this.state.activePageIdx + 1 };
    }

    private get maxPage(): number {
        return this.props.questionnaireStructure.pages[this.props.questionnaireStructure.pages.length - 1].pageNumber;
    }

    private renderPage = (page: JobOfferQuestionnairePage) => {
        const { t } = this.props;

        return (
            <>
                {this.maxPage > 1 && (
                    <h2 className='font-weight-bold text-dark'>
                        {t('jobApplication.stepHeading', {
                            step: this.state.activePageIdx + 1,
                            stepMax: this.maxPage,
                        })}
                    </h2>
                )}
                <QuestionnairePage
                    key={`questionnaire-page-${this.state.activePageIdx + 1}`}
                    page={page}
                    isLastPage={page.pageNumber === this.maxPage}
                    isSubmittingApplication={this.state.isSubmittingApplication}
                    validationSchema={yup.object(this.buildValidationSchema(page))}
                    storedAnswers={this.props.storedAnswers}
                    onSubmit={this.onPageSubmit}
                    onBack={this.onBack}
                    formErrors={this.props.formErrors}
                />
            </>
        );
    };

    private buildValidationSchema = (page: JobOfferQuestionnairePage) => {
        const { i18n } = this.props;
        const lang = i18n.language;

        let pageValidationSchema: Record<string, yup.Schema<any>> = _.reduce(
            page.elements,
            this.buildElementValidationSchema,
            {} as Record<string, yup.Schema<any>>
        );

        if (page.pageNumber === 1) {
            pageValidationSchema = {
                ...pageValidationSchema,
                firstName: yup.string().required(),
                lastName: yup.string().required(),
                prefix: yup.string().oneOf(prefixes[lang]).required(),
                phoneNumber: yup.string().required(),
                email: yup.string().email().required(),
            };
        }

        return pageValidationSchema;
    };

    private buildElementValidationSchema = (prev: Record<string, yup.Schema<any>>, curr: QuestionnaireElement) => {
        const elementSchema = this.getElementValidationSchema(curr);
        return { ...prev, ...elementSchema };
    };

    private getElementValidationSchema = (element: QuestionnaireElement): Record<string, yup.Schema<any>> | null => {
        switch (element.$type) {
            case QuestionQuestionnaireElementType.QuestionnaireAddress:
            case QuestionQuestionnaireElementType.QuestionnaireSingleChoice: {
                let validationSchema = yup.string();

                if (element.isRequired) {
                    validationSchema = validationSchema.required();
                }

                return { [element.id]: validationSchema };
            }
            case QuestionQuestionnaireElementType.QuestionnaireAttachment:
            case QuestionQuestionnaireElementType.QuestionnairePhoto: {
                let validationSchema = yup.mixed();

                if (element.isRequired) {
                    validationSchema = validationSchema.required();
                }

                return { [element.id]: validationSchema };
            }
            case QuestionQuestionnaireElementType.QuestionnaireCoverLetter:
            case QuestionQuestionnaireElementType.QuestionnaireSummary:
            case QuestionQuestionnaireElementType.QuestionnaireResume: {
                let validationSchema = yup.string().required();

                return { [element.id]: validationSchema };
            }
            case QuestionQuestionnaireElementType.QuestionnaireMultipleChoice: {
                let validationSchema = yup.array();

                if (element.isRequired) {
                    validationSchema = validationSchema.min(1);
                }

                return { [element.id]: validationSchema };
            }
            case QuestionQuestionnaireElementType.QuestionnaireMultiLineText:
            case QuestionQuestionnaireElementType.QuestionnaireSingleLineText: {
                let validationSchema = yup.string();

                if (element.isRequired) {
                    validationSchema = validationSchema.required();
                }

                return { [element.id]: validationSchema };
            }
            case QuestionQuestionnaireElementType.QuestionnaireEducation: {
                let validationSchema = yup
                    .array()
                    .of(
                        yup.object().shape({
                            school: yup.string().required(),
                            degree: yup.string().required(),
                            startDateMonth: yup.string().required(),
                            startDateYear: yup.string().required(),
                            endDateMonth: yup.string().when('currentlyStudyingHere', {
                                is: (val) => !val,
                                then: endDateMonthValidationSchema,
                                otherwise: yup.string().notRequired(),
                            }),
                            endDateYear: yup.string().when('currentlyStudyingHere', {
                                is: (val) => !val,
                                then: endDateYearValidationSchema,
                                otherwise: yup.string().notRequired(),
                            }),
                        })
                    )
                    .required();

                return { [element.id]: validationSchema };
            }
            case QuestionQuestionnaireElementType.QuestionnaireExperience: {
                let validationSchema = yup
                    .array()
                    .of(
                        yup.object().shape({
                            title: yup.string().required(),
                            company: yup.string().required(),
                            startDateMonth: yup.string().required(),
                            startDateYear: yup.string().required(),
                            endDateMonth: yup.string().when('currentlyWorkingInThisRole', {
                                is: (val) => !val,
                                then: endDateMonthValidationSchema,
                                otherwise: yup.string().notRequired(),
                            }),
                            endDateYear: yup.string().when('currentlyWorkingInThisRole', {
                                is: (val) => !val,
                                then: endDateYearValidationSchema,
                                otherwise: yup.string().notRequired(),
                            }),
                        })
                    )
                    .required();

                return { [element.id]: validationSchema };
            }
            default: {
                return null;
            }
        }
    };

    private onBack = (values: FormikValues) => {
        // save answers
        this.props.saveAdditionalAnswers(this.getAdditionalAnswersFromValues(values));

        // prev page
        this.setState((state) => ({
            ...state,
            activePageIdx: state.activePageIdx - 1,
        }));
    };

    @boundMethod
    private async onPageSubmit(values: FormikValues) {
        // store current page
        if (this.state.activePageIdx === 0) {
            this.props.saveStaticQuestionsAnswers(
                values.firstName,
                values.lastName,
                values.prefix,
                values.phoneNumber,
                values.email
            );
        }

        // save answers
        this.props.saveAdditionalAnswers(this.getAdditionalAnswersFromValues(values));

        if (this.state.activePageIdx + 1 >= this.maxPage) {
            this.setState({
                isSubmittingApplication: true,
            });

            // submit whole form
            const success = await this.props.submitForm();
            if (success) {
                // redirect to "thank you" page
                const redirectPath = RoutePath.jobOfferApplicationSent(this.props.match.params.id);
                this.props.history.push(redirectPath);
            } else {
                // find the first page with validation error and change current page to that one
                const pageWithValidationError = this.findFirstPageWithValidationError();

                this.setState((state) => ({
                    isSubmittingApplication: false,
                    activePageIdx: pageWithValidationError
                        ? pageWithValidationError.pageNumber - 1
                        : state.activePageIdx,
                }));
            }
        } else {
            // next page
            this.setState((state) => ({
                ...state,
                activePageIdx: state.activePageIdx + 1,
            }));
        }
    }

    private getAdditionalAnswersFromValues = (values: FormikValues): QuestionnaireAnswer[] => {
        return _.filter(_.map(values, this.getAnswerFromField), (item) => item) as QuestionnaireAnswer[];
    };

    private getAnswerFromField = (value: any, questionId: string): QuestionnaireAnswer | undefined => {
        const questionElement = this.currentPage.elements.find((element) => element.id === questionId);

        if (questionElement) {
            switch (questionElement.$type) {
                case QuestionQuestionnaireElementType.QuestionnaireAttachment:
                    return {
                        questionId,
                        $type: QuestionQuestionnaireAnswerType.QuestionnaireAttachmentAnswer,
                        fileIdentifier: (value as FileUploadValue).fileId,
                        fileName: (value as FileUploadValue).fileName,
                    };
                case QuestionQuestionnaireElementType.QuestionnaireCoverLetter:
                    return {
                        questionId,
                        $type: QuestionQuestionnaireAnswerType.QuestionnaireCoverLetterAnswer,
                        fileIdentifier: (value as FileUploadValue).fileId,
                        fileName: (value as FileUploadValue).fileName,
                    };
                case QuestionQuestionnaireElementType.QuestionnairePhoto:
                    return {
                        questionId,
                        $type: QuestionQuestionnaireAnswerType.QuestionnairePhotoAnswer,
                        fileIdentifier: (value as FileUploadValue).fileId,
                        fileName: (value as FileUploadValue).fileName,
                    };
                case QuestionQuestionnaireElementType.QuestionnaireResume:
                    return {
                        questionId,
                        $type: QuestionQuestionnaireAnswerType.QuestionnaireResumeAnswer,
                        fileIdentifier: (value as FileUploadValue).fileId,
                        fileName: (value as FileUploadValue).fileName,
                    };
                case QuestionQuestionnaireElementType.QuestionnaireMultipleChoice:
                    return {
                        questionId,
                        $type: QuestionQuestionnaireAnswerType.QuestionnaireMultipleChoiceAnswer,
                        chosenOptions: value,
                    };
                case QuestionQuestionnaireElementType.QuestionnaireSingleChoice:
                    // FIXME: cast to integer - API expects numeric type here
                    const parsedValue = value ? +value : undefined;

                    return {
                        questionId,
                        $type: QuestionQuestionnaireAnswerType.QuestionnaireSingleChoiceAnswer,
                        chosenOption: parsedValue,
                    };
                case QuestionQuestionnaireElementType.QuestionnaireAddress:
                    return {
                        questionId,
                        $type: QuestionQuestionnaireAnswerType.QuestionnaireAddressAnswer,
                        value,
                    };
                case QuestionQuestionnaireElementType.QuestionnaireMultiLineText:
                    return {
                        questionId,
                        $type: QuestionQuestionnaireAnswerType.QuestionnaireMultiLineTextAnswer,
                        value,
                    };
                case QuestionQuestionnaireElementType.QuestionnaireSingleLineText:
                    return {
                        questionId,
                        $type: QuestionQuestionnaireAnswerType.QuestionnaireSingleLineTextAnswer,
                        value,
                    };
                case QuestionQuestionnaireElementType.QuestionnaireSummary:
                    return {
                        questionId,
                        $type: QuestionQuestionnaireAnswerType.QuestionnaireSummaryAnswer,
                        value,
                    };
                case QuestionQuestionnaireElementType.QuestionnaireEducation: {
                    const values: EducationFormValues[] = value;

                    return {
                        questionId,
                        $type: QuestionQuestionnaireAnswerType.QuestionnaireEducationAnswer,
                        values: values.map((item: EducationFormValues, idx: number) => {
                            const startDate = moment(
                                `${item.startDateYear}-${item.startDateMonth.padStart(2)}-01`,
                                'YYYY-MM-DD'
                            ).format('YYYY-MM-DD');
                            let endDate = undefined;

                            if (!item['currentlyStudyingHere'] && item.endDateYear && item.endDateMonth) {
                                endDate = moment(
                                    `${item.endDateYear}-${item.endDateMonth.padStart(2)}-01`,
                                    'YYYY-MM-DD'
                                ).format('YYYY-MM-DD');
                            }

                            return {
                                ...item,
                                order: idx + 1,
                                startDate,
                                endDate,
                            };
                        }),
                    };
                }
                case QuestionQuestionnaireElementType.QuestionnaireExperience: {
                    const values: FormValues[] = value;

                    return {
                        questionId,
                        $type: QuestionQuestionnaireAnswerType.QuestionnaireExperienceAnswer,
                        values: values.map((item: FormValues, idx: number) => {
                            const startDate = moment(
                                `${item.startDateYear}-${item.startDateMonth.padStart(2)}-01`,
                                'YYYY-MM-DD'
                            ).format('YYYY-MM-DD');
                            let endDate = undefined;

                            if (!item['currentlyWorkingInThisRole'] && item.endDateYear && item.endDateMonth) {
                                endDate = moment(
                                    `${item.endDateYear}-${item.endDateMonth.padStart(2)}-01`,
                                    'YYYY-MM-DD'
                                ).format('YYYY-MM-DD');
                            }

                            return {
                                ...item,
                                order: idx + 1,
                                startDate,
                                endDate,
                            };
                        }),
                    };
                }
            }
        }
    };

    private findFirstPageWithValidationError = (): JobOfferQuestionnairePage | undefined => {
        const firstError = this.props.formErrors[0];
        if (firstError) {
            return this.props.questionnaireStructure.pages.find((page) =>
                page.elements.find((element) => element.id === firstError.causedBy)
            );
        }
    };
}

const endDateYearValidationSchema = yup
    .string()
    .when(
        ['startDateYear', 'startDateMonth', 'endDateMonth'],
        (startDateYear: string, startDateMonth: string, endDateMonth: string, schema: yup.StringSchema) => {
            if (!startDateYear || !startDateMonth || !endDateMonth) {
                return schema.required();
            }

            return schema
                .required()
                .test('endDateYear', 'general.validation.dateEndIsBeforeStart', (endDateYear: string): boolean => {
                    const startDate = moment(`${startDateYear}-${startDateMonth}`, 'YYYY-MM');
                    const endDate = moment(`${endDateYear}-${endDateMonth}`, 'YYYY-MM');
                    return endDate.isSameOrAfter(startDate);
                });
        }
    );

const endDateMonthValidationSchema = yup
    .string()
    .when(
        ['startDateYear', 'startDateMonth', 'endDateYear'],
        (startDateYear: string, startDateMonth: string, endDateYear: string, schema: yup.StringSchema) => {
            if (!startDateYear || !startDateMonth || !endDateYear) {
                return schema.required();
            }

            return schema
                .required()
                .test('endDateMonth', 'general.validation.dateEndIsBeforeStart', (endDateMonth: string): boolean => {
                    const startDate = moment(`${startDateYear}-${startDateMonth}`, 'YYYY-MM');
                    const endDate = moment(`${endDateYear}-${endDateMonth}`, 'YYYY-MM');
                    return endDate.isSameOrAfter(startDate);
                });
        }
    );

const mapStateToProps = (state: State): OfferApplicationFormStateProps => ({
    jobOfferId: state.jobOffers.list[0],
    formErrors: state.jobOfferApplication.formErrors,
    storedAnswers: state.jobOfferApplication.answers,
});

const mapDispatchToProps = (dispatch: ThunkDispatch): OfferApplicationFormDispatchProps => ({
    saveStaticQuestionsAnswers: (
        firstName: string,
        lastName: string,
        prefix: string,
        phoneNumber: string,
        email: string
    ) =>
        dispatch(JobOfferApplicationThunks.saveStaticQuestionsAnswers(firstName, lastName, prefix, phoneNumber, email)),
    saveAdditionalAnswers: (additionalAnswers: QuestionnaireAnswer[]) =>
        dispatch(JobOfferApplicationThunks.saveAdditionalAnswers(additionalAnswers)),
    submitForm: () => dispatch(JobOfferApplicationThunks.submitApplication()),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withTranslation()(OfferApplicationForm)));
